import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { BRAND } from '../config/brand';
import PasswordFields, { analyzePassword, passwordsMatch } from '../components/PasswordFields';

const AdminSignup = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { isAuthenticated, setSession } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);

  const passwordAnalysis = useMemo(
    () => analyzePassword(password, invite?.email || ''),
    [password, invite?.email]
  );
  const canSubmit =
    name.trim().length > 0 &&
    passwordAnalysis.isValid &&
    passwordsMatch(password, passwordConfirm);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    fetch(`${API_BASE}/auth/admin/validate-signup-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setInvite(data.data);
          setName(data.data.name || '');
        } else {
          toast.error(data.message || 'Invalid invite');
        }
      })
      .catch(() => toast.error('Failed to validate invite'))
      .finally(() => setValidating(false));
  }, [token]);

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  if (validating) return <p className="p-8 text-center">Validating invite…</p>;
  if (!token || !invite) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md text-center">
          <p className="text-slate-700">Invalid or expired invite link.</p>
          <Link to="/login" className="text-teal-700 mt-4 inline-block hover:underline">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please meet all password requirements');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/admin/complete-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name }),
      });
      const data = await res.json();
      if (data.success) {
        setSession(data.data.token, data.data.user);
        toast.success('Account created — welcome!');
        navigate('/dashboard', { replace: true });
        return;
      }
      toast.error(data.message || 'Signup failed');
    } catch {
      toast.error('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <form onSubmit={submit} className="bg-white p-8 rounded-xl shadow-md w-full max-w-md">
        <h1 className="text-2xl font-semibold text-teal-800 mb-1">{BRAND.companyName}</h1>
        <p className="text-slate-600 mb-6">Create your admin password</p>
        <p className="text-sm text-slate-600 mb-4">
          Invited as <strong>{invite.email}</strong>
        </p>

        <label className="block text-sm font-medium text-slate-700 mb-1">Your name</label>
        <input
          type="text"
          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <PasswordFields
          password={password}
          confirmPassword={passwordConfirm}
          email={invite.email}
          onPasswordChange={setPassword}
          onConfirmChange={setPasswordConfirm}
        />

        <button
          type="submit"
          disabled={loading || !canSubmit}
          className="w-full mt-6 bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
};

export default AdminSignup;
